import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { Trophy, Loader2, Layers, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import LeaderboardPagination from "components/LeaderboardPagination";
import {
  useGetPlayTableHistoryQuery,
  useGetComboWinnersQuery,
  useGetProductWinningMutation,
  useGetProductComboWinningMutation,
  api,
} from "state/api";
import { getImageUrl } from "utils/common";
import {
  WIN_GREEN,
  BRAND_RED,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  LB_BORDER,
  PHONE_ORANGE,
  PRIZE_ORANGE,
  maskPhone,
  entryDisplayName,
  initialsFromName,
  leaderboardDisplayRank,
  formatLeaderboardTime,
  formatLeaderboardTimeFull,
  shouldShowLeaderboardQuestions,
  sortedWinningDocuments,
  parseWinRange,
  isRankInWinRange,
  isProductPrizeTier,
  winnersForWinRange,
  calculateDivision,
  standardPrizeLabel,
  comboWinningRowTitle,
  comboPrizeLabel,
  payoutLabelForRow,
} from "utils/prizeDisplay";
import {
  prefetchLeaderboardPage,
  useLeaderboardPageTransition,
} from "hooks/useLeaderboardPageTransition";
import useWinningHistoryPagination from "hooks/useWinningHistoryPagination";

export const WISHAUR_RED = "#D32F2F";

const LEADERBOARD_PAGE_SIZE = 50;

const Avatar = ({ initials, image, size = 28 }) => {
  const [imgError, setImgError] = useState(false);
  const src = getImageUrl(image);

  useEffect(() => {
    setImgError(false);
  }, [image]);

  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0 overflow-hidden"
      style={{
        width: size,
        height: size,
        fontSize: size <= 24 ? 10 : 11,
        backgroundColor: "#E2E8F0",
        color: TEXT_SECONDARY,
      }}
    >
      {src && !imgError ? (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : initials ? (
        <span className="font-semibold text-gray-700">{initials}</span>
      ) : (
        <User style={{ width: size * 0.5, height: size * 0.5 }} />
      )}
    </div>
  );
};

const LeaderboardRow = ({ row, product, winningDocs, showPrize, dark = false }) => {
  const name = entryDisplayName(row);
  const displayRank = leaderboardDisplayRank(row);
  const timeLabel = formatLeaderboardTime(row);
  const timeTitle = formatLeaderboardTimeFull(row);
  const showQuestions = shouldShowLeaderboardQuestions(row);
  const prizeLabel =
    showPrize || row.payoutAmount || row.payoutStatus === "PRODUCT"
      ? payoutLabelForRow(row, product, winningDocs)
      : null;
  const border = dark ? "#2a3548" : LB_BORDER;
  const primary = dark ? "#f8fafc" : TEXT_PRIMARY;

  return (
    <div
      className="flex items-center gap-1.5 px-2 py-2 border-b last:border-b-0 min-h-[52px]"
      style={{ borderColor: border }}
    >
      <Avatar
        initials={initialsFromName(name)}
        image={row.userDetails?.image}
        size={28}
      />
      <div className="min-w-0 flex-[1.4]">
        <p
          className="text-[11px] font-semibold truncate leading-tight"
          style={{ color: primary }}
          title={name}
        >
          {name}
        </p>
        {row.userDetails?.phone && (
          <p
            className="text-[9px] font-semibold truncate leading-tight mt-0.5"
            style={{ color: PHONE_ORANGE }}
          >
            {maskPhone(row.userDetails.phone)}
          </p>
        )}
      </div>
      <div className="flex-[1.1] min-w-[72px] text-center shrink-0">
        <p
          className="text-[10px] font-semibold font-mono leading-tight whitespace-nowrap"
          style={{ color: primary }}
          title={timeTitle}
        >
          {timeLabel}
        </p>
        {showQuestions && (
          <p
            className="text-[10px] font-semibold leading-tight mt-0.5 whitespace-nowrap"
            style={{ color: BRAND_RED }}
          >
            Que {row.correctQuestion ?? "—"}/{row.totalQuestion ?? "—"}
          </p>
        )}
      </div>
      <div className="flex-[0.85] min-w-[44px] text-right shrink-0">
        <p className="text-sm font-bold leading-tight" style={{ color: WIN_GREEN }}>
          {displayRank}
        </p>
        {prizeLabel && (
          <p
            className="text-[10px] font-bold leading-tight mt-0.5 break-words"
            style={{ color: PRIZE_ORANGE }}
            title={prizeLabel}
          >
            {prizeLabel}
          </p>
        )}
      </div>
    </div>
  );
};

const WINNINGS_GRID_RANK = "56px 1fr 72px";
const WINNINGS_GRID_COMBO = "minmax(108px, max-content) 1fr 72px";

const WinningRow = ({ rankLabel, winners, amountLabel, onShowWinners, isCombo = false }) => {
  const primary = winners[0];
  const isMulti = winners.length > 1;
  const canExpand = winners.length > 0;

  return (
    <button
      type="button"
      disabled={!canExpand}
      onClick={() => canExpand && onShowWinners?.({ rankLabel, winners, amountLabel })}
      className={`w-full grid items-center gap-1 px-2 py-2 border-b last:border-b-0 text-left transition-colors ${
        canExpand ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"
      }`}
      style={{
        borderColor: LB_BORDER,
        gridTemplateColumns: isCombo ? WINNINGS_GRID_COMBO : WINNINGS_GRID_RANK,
      }}
    >
      <p
        className={`text-[11px] font-bold ${isCombo ? "whitespace-nowrap" : ""}`}
        style={{ color: WIN_GREEN }}
      >
        {rankLabel}
      </p>
      <div className="min-w-0 text-center">
        {primary ? (
          <>
            <p className="text-xs font-bold truncate" style={{ color: TEXT_PRIMARY }}>
              {entryDisplayName(primary.entry)}
            </p>
            {isMulti ? (
              <p className="text-[10px] font-semibold" style={{ color: WISHAUR_RED }}>
                {winners.length} winners · tap to view
              </p>
            ) : (
              primary.entry.userDetails?.phone && (
                <p className="text-[10px] truncate" style={{ color: TEXT_SECONDARY }}>
                  {maskPhone(primary.entry.userDetails.phone)}
                </p>
              )
            )}
          </>
        ) : (
          <p className="text-[11px]" style={{ color: TEXT_SECONDARY }}>
            No player yet
          </p>
        )}
      </div>
      <p className="text-xs font-bold text-right truncate" style={{ color: WIN_GREEN }}>
        {amountLabel}
      </p>
    </button>
  );
};

const ComboHeader = () => (
  <div
    className="mx-2 mt-2 mb-1 px-2.5 py-2 rounded-lg border text-[11px] font-bold flex items-start gap-2"
    style={{ backgroundColor: "#EEF2FF", borderColor: "#C7D2FE", color: "#3730A3" }}
  >
    <Layers className="w-3.5 h-3.5 shrink-0 mt-0.5" />
    <span>Combo Winnings — hit all ranks (across attempts) to win</span>
  </div>
);

const formatRankNumber = (rank) => {
  const n = Number(rank) || 0;
  if (n < 1) return "—";
  return n.toLocaleString("en-IN");
};

/** Full-panel Main Rank for live board — product prize rank or any lookup rank. */
const MainRankSpotlight = ({
  product,
  rank,
  entry,
  amountLabel,
  loading,
  isCustomLookup = false,
  dark = false,
}) => {
  const bg = "transparent";
  const text = dark ? "#f8fafc" : "#0f172a";
  const muted = dark ? "#94a3b8" : "#0f766e";
  const panel = dark ? "rgba(19,78,74,0.45)" : "rgba(204,251,241,0.65)";
  const border = dark ? "rgba(45,212,191,0.22)" : "rgba(13,148,136,0.22)";
  const prizeName =
    String(amountLabel || product?.productName || "Product prize").trim() ||
    "Product prize";
  const target = Number(rank) || 0;
  const winnerName = entry ? entryDisplayName(entry) : "";
  const timeLabel = entry ? formatLeaderboardTime(entry) : "";
  const timeTitle = entry ? formatLeaderboardTimeFull(entry) : "";
  const showQuestions = entry ? shouldShowLeaderboardQuestions(entry) : false;

  if (loading) {
    return (
      <div
        className="h-full flex flex-col items-center justify-center gap-2 px-4"
        style={{ backgroundColor: bg }}
      >
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: WISHAUR_RED }} />
        <p className="text-xs font-medium" style={{ color: muted }}>
          {isCustomLookup ? "Loading rank…" : "Loading main rank…"}
        </p>
      </div>
    );
  }

  if (!target) {
    return (
      <div
        className="h-full flex flex-col items-center justify-center gap-2 px-6 text-center"
        style={{ backgroundColor: bg }}
      >
        <Trophy className="w-8 h-8" style={{ color: muted }} />
        <p className="text-sm font-bold" style={{ color: text }}>
          No product main rank configured
        </p>
        <p className="text-xs" style={{ color: muted }}>
          Add a PRODUCT prize rank in product winnings, or look up any rank
          from the right panel.
        </p>
      </div>
    );
  }

  return (
    <div
      className="h-full min-h-0 flex flex-col overflow-hidden animate-in fade-in duration-300 px-4 py-4 gap-4"
      style={{ backgroundColor: bg }}
    >
      <div className="text-center shrink-0">
        <p
          className="text-[10px] font-bold uppercase tracking-[0.14em]"
          style={{ color: muted }}
        >
          {isCustomLookup ? "Rank lookup" : "Winning rank"}
        </p>
        <p
          className="mt-1.5 text-3xl sm:text-4xl font-black tabular-nums leading-none tracking-tight"
          style={{ color: WIN_GREEN }}
        >
          #{formatRankNumber(target)}
        </p>
        {prizeName ? (
          <p
            className="mt-2 text-sm font-bold leading-tight line-clamp-2"
            style={{ color: text }}
            title={prizeName}
          >
            {prizeName}
          </p>
        ) : null}
      </div>

      {/* Winner block — fills remaining space */}
      <div
        className="flex-1 min-h-0 flex flex-col justify-center rounded-xl border px-4 py-5"
        style={{ backgroundColor: panel, borderColor: border }}
      >
        <p
          className="text-[10px] font-bold uppercase tracking-wider mb-3 text-center"
          style={{ color: muted }}
        >
          Current holder
        </p>
        {entry ? (
          <div className="flex flex-col items-center text-center gap-3">
            <Avatar
              initials={initialsFromName(winnerName)}
              image={entry.userDetails?.image}
              size={72}
            />
            <div className="min-w-0 w-full">
              <p
                className="text-lg sm:text-xl font-black truncate leading-tight"
                style={{ color: text }}
                title={winnerName}
              >
                {winnerName}
              </p>
              {entry.userDetails?.phone ? (
                <p
                  className="text-xs font-semibold mt-1 truncate"
                  style={{ color: PHONE_ORANGE }}
                >
                  {maskPhone(entry.userDetails.phone)}
                </p>
              ) : null}
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2">
                {timeLabel ? (
                  <span
                    className="text-xs font-mono font-semibold"
                    style={{ color: text }}
                    title={timeTitle}
                  >
                    {timeLabel}
                  </span>
                ) : null}
                {showQuestions ? (
                  <span
                    className="text-xs font-bold"
                    style={{ color: BRAND_RED }}
                  >
                    Que {entry.correctQuestion ?? "—"}/
                    {entry.totalQuestion ?? "—"}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center gap-3 py-2">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center shrink-0"
              style={{
                backgroundColor: dark ? "#334155" : "#e2e8f0",
                color: muted,
              }}
            >
              <User className="w-7 h-7" />
            </div>
            <div className="min-w-0 max-w-[18rem]">
              <p className="text-base font-bold" style={{ color: text }}>
                No player yet
              </p>
              <p className="text-[11px] mt-1 leading-snug" style={{ color: muted }}>
                Rank #{formatRankNumber(target)} unlocks when the board reaches
                that position.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const WinnersPopover = ({ detail, onClose }) => {
  if (!detail) return null;
  const { rankLabel, winners, amountLabel } = detail;

  return (
    <div className="absolute inset-0 z-20 bg-black/30 flex items-end">
      <div className="w-full max-h-[70%] bg-white rounded-t-xl shadow-xl flex flex-col">
        <div className="px-3 py-2.5 border-b flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-bold truncate" style={{ color: TEXT_PRIMARY }}>
              {rankLabel}
            </p>
            <p className="text-[11px]" style={{ color: TEXT_SECONDARY }}>
              {winners.length} player{winners.length === 1 ? "" : "s"} · {amountLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200"
          >
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {winners.map(({ entry, rank }) => (
            <div
              key={`${entry._id}-${rank}`}
              className="flex items-center gap-2 p-2 rounded-lg border bg-slate-50"
              style={{ borderColor: LB_BORDER }}
            >
              <Avatar
                initials={initialsFromName(entryDisplayName(entry))}
                image={entry.userDetails?.image}
                size={32}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{entryDisplayName(entry)}</p>
                {entry.userDetails?.phone && (
                  <p className="text-xs" style={{ color: TEXT_SECONDARY }}>
                    {maskPhone(entry.userDetails.phone)}
                  </p>
                )}
              </div>
              <p className="text-sm font-extrabold" style={{ color: WIN_GREEN }}>
                {leaderboardDisplayRank({ ...entry, rank })}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const LiveContestSidebar = ({
  product,
  selectedWish,
  fallbackHistory = [],
  fallbackLoading = false,
  refreshTrigger = 0,
  realtimeConnected = false,
  embedded = false,
  /** When set (>=1), show this rank instead of the product main prize rank. */
  viewRank = null,
  className = "",
  pollingIntervalMs,
  boardTheme = "light",
}) => {
  const darkBoard = boardTheme === "dark";
  const boardBorder = darkBoard ? "#2a3548" : LB_BORDER;
  const boardBg = darkBoard ? "#141a26" : "#ffffff";
  const boardText = darkBoard ? "#f8fafc" : TEXT_PRIMARY;
  const boardMuted = darkBoard ? "#94a3b8" : TEXT_SECONDARY;
  // Main Rank (embedded) shows only the product prize rank — not the full board.
  const [mainTab, setMainTab] = useState(embedded ? "winnings" : "leaderboard");
  const [winningsTab, setWinningsTab] = useState("rank");
  const [winningDocs, setWinningDocs] = useState([]);
  const [comboDocs, setComboDocs] = useState([]);
  const [winnerDetail, setWinnerDetail] = useState(null);
  const [mainRankEntry, setMainRankEntry] = useState(null);
  const [mainRankLoading, setMainRankLoading] = useState(false);
  const [mainRankLoaded, setMainRankLoaded] = useState(false);
  const winningsScrollRef = useRef(null);
  const mainRankFetchRef = useRef(0);

  const productId = product?._id;
  const playToken = selectedWish?.playToken;
  const isContestMode = Boolean(productId && playToken);
  const dispatch = useDispatch();
  const [historyPage, setHistoryPage] = useState(1);
  const [autoPaginate, setAutoPaginate] = useState(false);

  const [fetchWinnings] = useGetProductWinningMutation();
  const [fetchComboWinnings] = useGetProductComboWinningMutation();

  const {
    data: liveHistoryData,
    isLoading: liveHistoryLoading,
    isFetching: liveHistoryFetching,
  } = useGetPlayTableHistoryQuery(
    { productId, playToken, page: historyPage, pageSize: LEADERBOARD_PAGE_SIZE },
    {
      skip: !isContestMode,
      pollingInterval:
        typeof pollingIntervalMs === "number"
          ? pollingIntervalMs
          : realtimeConnected
            ? 30000
            : 10000,
    }
  );

  const historyTotalCount = liveHistoryData?.totalCount ?? 0;
  const historyRankedCount =
    liveHistoryData?.rankedCount ?? liveHistoryData?.totalCount ?? 0;
  const historyTotalPages = Math.max(
    1,
    Math.ceil(historyTotalCount / LEADERBOARD_PAGE_SIZE)
  );

  const hasCombo = comboDocs.length > 0;

  const { data: comboWinnersData, isLoading: comboWinnersLoading } = useGetComboWinnersQuery(
    { productId, playToken },
    {
      skip: !isContestMode || !hasCombo,
      pollingInterval: realtimeConnected ? 60000 : 30000,
    }
  );

  const loadPrizeDocs = useCallback(async () => {
    if (!productId) {
      setWinningDocs([]);
      setComboDocs([]);
      return;
    }
    try {
      const [winnings, combos] = await Promise.all([
        fetchWinnings(productId).unwrap(),
        fetchComboWinnings(productId).unwrap(),
      ]);
      setWinningDocs(
        sortedWinningDocuments(
          Array.isArray(winnings)
            ? winnings
            : Array.isArray(winnings?.data)
              ? winnings.data
              : []
        )
      );
      setComboDocs(
        Array.isArray(combos)
          ? combos
          : Array.isArray(combos?.data)
            ? combos.data
            : []
      );
    } catch {
      setWinningDocs([]);
      setComboDocs([]);
    }
  }, [productId, fetchWinnings, fetchComboWinnings]);

  useEffect(() => {
    loadPrizeDocs();
  }, [loadPrizeDocs]);

  useEffect(() => {
    if (!refreshTrigger || !isContestMode || !productId || !playToken) return;
    dispatch(
      api.endpoints.getPlayTableHistory.initiate(
        { productId, playToken, page: historyPage, pageSize: LEADERBOARD_PAGE_SIZE },
        { forceRefetch: true }
      )
    );
    if (comboDocs.length > 0) {
      dispatch(
        api.endpoints.getComboWinners.initiate(
          { productId, playToken },
          { forceRefetch: true }
        )
      );
    }
    loadPrizeDocs();
  }, [refreshTrigger, isContestMode, productId, playToken, historyPage, comboDocs.length, dispatch, loadPrizeDocs]);

  useEffect(() => {
    setMainTab(embedded ? "winnings" : "leaderboard");
    setWinningsTab("rank");
    setWinnerDetail(null);
    setHistoryPage(1);
    setAutoPaginate(false);
    setMainRankEntry(null);
    setMainRankLoaded(false);
    setMainRankLoading(false);
  }, [playToken, embedded]);

  const mainProductWinningDoc = useMemo(
    () =>
      winningDocs.find((doc) => isProductPrizeTier(doc, product, winningDocs)) ||
      null,
    [winningDocs, product]
  );

  const mainProductRank = useMemo(() => {
    if (!mainProductWinningDoc) return 0;
    const { start } = parseWinRange(mainProductWinningDoc.winrange);
    return start >= 1 ? start : 0;
  }, [mainProductWinningDoc]);

  const isCustomLookup = Number(viewRank) >= 1;
  const displayRank = useMemo(() => {
    if (isCustomLookup) return Math.floor(Number(viewRank));
    return mainProductRank;
  }, [isCustomLookup, viewRank, mainProductRank]);

  const displayPrizeLabel = useMemo(() => {
    if (!isCustomLookup) {
      return String(product?.productName || "").trim() || "Product prize";
    }
    const tier = winningDocs.find((doc) =>
      isRankInWinRange(displayRank, doc.winrange)
    );
    if (!tier) return "Rank lookup";
    if (isProductPrizeTier(tier, product, winningDocs)) {
      return String(product?.productName || "").trim() || "Product prize";
    }
    const calculated = calculateDivision(tier.winrange, tier.amount);
    return standardPrizeLabel(
      tier,
      { ...product, productWinningDocuments: winningDocs },
      calculated
    );
  }, [
    isCustomLookup,
    product,
    winningDocs,
    displayRank,
  ]);

  // Embedded Main Rank: fetch one rank (product main, or lookup override).
  useEffect(() => {
    if (!embedded || !isContestMode || !productId || !playToken) return;

    if (displayRank < 1) {
      setMainRankEntry(null);
      setMainRankLoaded(true);
      setMainRankLoading(false);
      return;
    }

    // Wait until we know how many ranked players exist.
    if (!liveHistoryData && (liveHistoryLoading || liveHistoryFetching)) return;

    const ranked = Number(historyRankedCount) || 0;
    // Never skip to a huge empty page (e.g. car at #42_000_000 with ~45k players).
    if (displayRank > ranked) {
      setMainRankEntry(null);
      setMainRankLoaded(true);
      setMainRankLoading(false);
      return;
    }

    const fetchId = ++mainRankFetchRef.current;
    setMainRankLoading(true);
    setMainRankLoaded(false);

    dispatch(
      api.endpoints.getPlayTableHistory.initiate(
        {
          productId,
          playToken,
          page: displayRank,
          pageSize: 1,
        },
        { forceRefetch: true }
      )
    )
      .unwrap()
      .then((response) => {
        if (fetchId !== mainRankFetchRef.current) return;
        const row = response?.history?.[0];
        const rank = Number(row?.rank) || 0;
        setMainRankEntry(rank === displayRank ? row : null);
        setMainRankLoaded(true);
      })
      .catch(() => {
        if (fetchId !== mainRankFetchRef.current) return;
        setMainRankEntry(null);
        setMainRankLoaded(true);
      })
      .finally(() => {
        if (fetchId !== mainRankFetchRef.current) return;
        setMainRankLoading(false);
      });
  }, [
    embedded,
    isContestMode,
    productId,
    playToken,
    displayRank,
    historyRankedCount,
    liveHistoryData,
    liveHistoryLoading,
    liveHistoryFetching,
    dispatch,
    refreshTrigger,
  ]);

  const paginatedHistory = useMemo(() => {
    if (liveHistoryData?.history?.length) return liveHistoryData.history;
    if (liveHistoryData?.message === "No matching play table history found") return [];
    if (!isContestMode && fallbackHistory.length) return fallbackHistory;
    if (historyPage === 1 && !liveHistoryFetching && fallbackHistory.length) return fallbackHistory;
    return [];
  }, [liveHistoryData, fallbackHistory, isContestMode, historyPage, liveHistoryFetching]);

  const {
    historyByRank,
    loading: winningHistoryLoading,
    loadingMore: winningHistoryLoadingMore,
    loaded: winningHistoryLoaded,
    hasMore: winningHistoryHasMore,
    loadInitial: loadWinningHistory,
    loadMore: loadMoreWinningHistory,
    reset: resetWinningHistory,
  } = useWinningHistoryPagination({
    productId,
    playToken,
    winningDocs,
    seedHistory: paginatedHistory,
    rankCeiling: historyRankedCount,
  });

  useEffect(() => {
    resetWinningHistory();
  }, [playToken, resetWinningHistory]);

  useEffect(() => {
    if (refreshTrigger) {
      resetWinningHistory();
    }
  }, [refreshTrigger, resetWinningHistory]);

  useEffect(() => {
    // Embedded Main Rank uses a single-rank fetch, not full winnings history.
    if (embedded) return;
    if (mainTab !== "winnings" || winningsTab !== "rank") return;
    if (!isContestMode || winningDocs.length === 0) return;
    if (!winningHistoryLoaded && !winningHistoryLoading) {
      loadWinningHistory();
    }
  }, [
    embedded,
    mainTab,
    winningsTab,
    isContestMode,
    winningDocs.length,
    winningHistoryLoaded,
    winningHistoryLoading,
    loadWinningHistory,
  ]);

  const onWinningsScroll = useCallback(() => {
    const el = winningsScrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      loadMoreWinningHistory();
    }
  }, [loadMoreWinningHistory]);

  useEffect(() => {
    if (mainTab !== "winnings" || winningsTab !== "rank") return;
    const el = winningsScrollRef.current;
    if (!el || winningHistoryLoading || winningHistoryLoadingMore) return;
    if (!winningHistoryHasMore) return;
    if (el.scrollHeight <= el.clientHeight + 8) {
      loadMoreWinningHistory();
    }
  }, [
    mainTab,
    winningsTab,
    winningHistoryLoading,
    winningHistoryLoadingMore,
    winningHistoryHasMore,
    winningHistoryLoaded,
    loadMoreWinningHistory,
  ]);

  const historyLoading = isContestMode
    ? liveHistoryLoading && paginatedHistory.length === 0
    : fallbackLoading && paginatedHistory.length === 0;
  const pageTransition = useLeaderboardPageTransition(
    historyPage,
    liveHistoryFetching,
    liveHistoryLoading,
    paginatedHistory.length > 0
  );
  // Keep prior rows visible during background poll refresh.
  const showPageLoader =
    historyLoading || (pageTransition && paginatedHistory.length === 0);
  const historyRefreshing =
    isContestMode && liveHistoryFetching && paginatedHistory.length > 0 && !pageTransition;

  useEffect(() => {
    if (!autoPaginate || !isContestMode || historyTotalPages <= 1) return;
    const nextPage = historyPage >= historyTotalPages ? 1 : historyPage + 1;
    prefetchLeaderboardPage(dispatch, api, {
      productId,
      playToken,
      page: nextPage,
      pageSize: LEADERBOARD_PAGE_SIZE,
    });
  }, [
    autoPaginate,
    isContestMode,
    historyPage,
    historyTotalPages,
    productId,
    playToken,
    dispatch,
  ]);

  const historyByRankForRows = historyByRank;
  const showPrize = product?.isTimeBasedEvent === true;

  const comboWinnersByRanks = useMemo(() => {
    const map = {};
    for (const combo of comboWinnersData?.combos ?? []) {
      map[combo.comboRanks] = (combo.winners ?? []).map((entry) => ({
        entry,
        rank: entry.rank,
      }));
    }
    return map;
  }, [comboWinnersData]);

  const rankWinningRows = useMemo(() => {
    // Live Main Rank panel: only the product (PRODUCT) prize rank.
    if (embedded) {
      if (!mainProductWinningDoc) return [];
      const calculated = calculateDivision(
        mainProductWinningDoc.winrange,
        mainProductWinningDoc.amount
      );
      return [
        {
          id: mainProductWinningDoc._id,
          rankLabel: `#${mainProductWinningDoc.winrange}`,
          winners: mainRankEntry
            ? [{ entry: mainRankEntry, rank: mainProductRank }]
            : [],
          amountLabel: standardPrizeLabel(
            mainProductWinningDoc,
            { ...product, productWinningDocuments: winningDocs },
            calculated
          ),
        },
      ];
    }

    return winningDocs.map((doc) => {
      const winners = winnersForWinRange(doc.winrange, historyByRankForRows);
      const calculated = calculateDivision(doc.winrange, doc.amount);
      return {
        id: doc._id,
        rankLabel: `#${doc.winrange}`,
        winners,
        amountLabel: standardPrizeLabel(
          doc,
          { ...product, productWinningDocuments: winningDocs },
          calculated
        ),
      };
    });
  }, [
    winningDocs,
    historyByRankForRows,
    product,
    embedded,
    mainProductWinningDoc,
    mainRankEntry,
    mainProductRank,
  ]);

  const comboWinningRows = useMemo(
    () =>
      comboDocs.map((combo) => ({
        id: combo._id,
        rankLabel: comboWinningRowTitle({
          comboRanks: combo.comboRanks,
          label: combo.label,
          prizeType: combo.prizeType,
        }),
        winners: comboWinnersByRanks[combo.comboRanks] ?? [],
        amountLabel: comboPrizeLabel({
          comboRanks: combo.comboRanks,
          prizeType: combo.prizeType,
          productName: product?.productName,
          amount: combo.amount,
          productLabel: combo.label,
        }),
      })),
    [comboDocs, comboWinnersByRanks, product]
  );

  const outerClass = embedded
    ? "flex flex-col min-h-0 flex-1 h-full overflow-hidden relative"
    : "lg:col-span-4 flex flex-col min-h-[28rem] sm:min-h-[32rem] lg:min-h-0 lg:h-full lg:overflow-hidden relative";

  return (
    <div className={`${outerClass} ${className}`.trim()}>
      <Card
        className={`border-0 flex flex-col flex-1 min-h-0 overflow-hidden ${
          embedded ? "shadow-none rounded-none" : "shadow-sm rounded-xl"
        }`}
        style={{ backgroundColor: boardBg, color: boardText }}
      >
        {!embedded && (
          <CardHeader
            className="py-2 px-2 shrink-0 space-y-2 border-b"
            style={{ borderColor: boardBorder, backgroundColor: boardBg }}
          >
            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ color: boardText }}>
              <Trophy className="w-4 h-4" style={{ color: WISHAUR_RED }} />
              Live Leaderboard
              {historyRefreshing && (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 ml-auto" />
              )}
            </CardTitle>
            <div
              className="flex rounded-lg p-0.5"
              style={{ backgroundColor: darkBoard ? "#1e293b" : "#f3f4f6" }}
            >
              {[
                { id: "leaderboard", label: "Leaderboard" },
                { id: "winnings", label: "Winnings" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setMainTab(tab.id)}
                  className="flex-1 text-[11px] font-bold py-1.5 rounded-md transition-colors"
                  style={
                    mainTab === tab.id
                      ? {
                          backgroundColor: darkBoard ? "#334155" : "#ffffff",
                          color: boardText,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        }
                      : { color: boardMuted }
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {mainTab === "winnings" && hasCombo && (
              <div className="flex rounded-lg bg-indigo-50 p-0.5 border border-indigo-100">
                {[
                  { id: "rank", label: "Rank prizes" },
                  { id: "combo", label: "Combo" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setWinningsTab(tab.id)}
                    className={`flex-1 text-[10px] font-bold py-1 rounded-md transition-colors ${
                      winningsTab === tab.id
                        ? "bg-white text-indigo-900 shadow-sm"
                        : "text-indigo-600/70 hover:text-indigo-800"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </CardHeader>
        )}

        <CardContent className="p-0 flex-1 min-h-0 overflow-hidden flex flex-col">
          {embedded ? (
            <MainRankSpotlight
              product={product}
              rank={displayRank}
              entry={mainRankEntry}
              amountLabel={displayPrizeLabel}
              isCustomLookup={isCustomLookup}
              loading={
                displayRank >= 1 && (mainRankLoading || !mainRankLoaded)
              }
              dark={darkBoard}
            />
          ) : mainTab === "leaderboard" ? (
            <>
              <div
                className="flex items-center gap-1.5 px-2 py-1.5 shrink-0 border-b sticky top-0 z-10"
                style={{ borderColor: boardBorder, backgroundColor: boardBg }}
              >
                <span className="w-7 shrink-0" />
                <span className="text-xs font-bold flex-[1.4] min-w-0" style={{ color: boardText }}>
                  UserName
                </span>
                <span
                  className="text-xs font-bold flex-[1.1] min-w-[72px] text-center shrink-0"
                  style={{ color: boardText }}
                >
                  Best Time
                </span>
                <span
                  className="text-xs font-bold flex-[0.85] min-w-[44px] text-right shrink-0"
                  style={{ color: boardText }}
                >
                  Rank
                </span>
              </div>
              <div
                key={historyPage}
                className="relative flex-1 min-h-0 overflow-y-auto overflow-x-auto"
                style={{ backgroundColor: boardBg }}
              >
                {showPageLoader ? (
                  <div
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2"
                    style={{
                      backgroundColor: darkBoard ? "rgba(20,26,38,0.92)" : "rgba(255,255,255,0.9)",
                    }}
                  >
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                    <p className="text-[11px] font-medium" style={{ color: boardMuted }}>
                      Loading page {historyPage}…
                    </p>
                  </div>
                ) : paginatedHistory.length === 0 ? (
                  <p className="text-xs text-center py-8" style={{ color: boardMuted }}>
                    No ranked players yet.
                  </p>
                ) : (
                  <div className="animate-in fade-in duration-200">
                    {paginatedHistory.map((row) => (
                      <LeaderboardRow
                        key={row._id}
                        row={row}
                        product={product}
                        winningDocs={winningDocs}
                        showPrize={showPrize}
                        dark={darkBoard}
                      />
                    ))}
                  </div>
                )}
              </div>
              <LeaderboardPagination
                page={historyPage}
                totalPages={historyTotalPages}
                totalCount={historyTotalCount}
                pageSize={LEADERBOARD_PAGE_SIZE}
                loading={historyLoading}
                fetching={pageTransition}
                onPageChange={setHistoryPage}
                autoPlay={autoPaginate && mainTab === "leaderboard"}
                onAutoPlayChange={setAutoPaginate}
                compact
                dark={darkBoard}
              />
            </>
          ) : (
            <div
              ref={winningsScrollRef}
              className="flex-1 min-h-0 overflow-y-auto"
              onScroll={winningsTab === "rank" ? onWinningsScroll : undefined}
            >
              {winningsTab === "combo" && hasCombo && <ComboHeader />}
              {winningHistoryLoading && winningsTab === "rank" ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
                  <p className="text-[11px] text-gray-500">Loading winners by rank…</p>
                </div>
              ) : (winningsTab === "combo" ? comboWinningRows : rankWinningRows).length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-8">
                  {winningsTab === "combo"
                    ? "No combo winnings configured."
                    : "No rank prizes configured."}
                </p>
              ) : (
                <>
                  <div
                    className="grid gap-1 px-2 py-1.5 shrink-0 border-b bg-white sticky top-0 z-10"
                    style={{
                      borderColor: LB_BORDER,
                      gridTemplateColumns:
                        winningsTab === "combo" ? WINNINGS_GRID_COMBO : WINNINGS_GRID_RANK,
                    }}
                  >
                    <span className="text-[10px] font-bold uppercase" style={{ color: TEXT_SECONDARY }}>
                      {winningsTab === "combo" ? "Combo" : "Rank"}
                    </span>
                    <span className="text-[10px] font-bold uppercase text-center" style={{ color: TEXT_SECONDARY }}>
                      Winner
                    </span>
                    <span className="text-[10px] font-bold uppercase text-right" style={{ color: TEXT_SECONDARY }}>
                      Prize
                    </span>
                  </div>
                  {(winningsTab === "combo" ? comboWinningRows : rankWinningRows).map((row) => (
                    <WinningRow
                      key={row.id}
                      rankLabel={row.rankLabel}
                      winners={row.winners}
                      amountLabel={row.amountLabel}
                      onShowWinners={setWinnerDetail}
                      isCombo={winningsTab === "combo"}
                    />
                  ))}
                  {winningsTab === "rank" && winningHistoryLoadingMore && (
                    <div className="flex justify-center py-3">
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    </div>
                  )}
                  {winningsTab === "combo" && comboWinnersLoading && (
                    <div className="flex justify-center py-3">
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <WinnersPopover detail={winnerDetail} onClose={() => setWinnerDetail(null)} />
    </div>
  );
};

export default LiveContestSidebar;
